import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Button, DatePicker, Divider, Flex, Form, InputNumber } from 'antd';
import { ColumnProps } from 'antd/lib/table';
import dayjs from 'dayjs';

import AddBuildingModal from '@totem/components/services/remoteAccess/auditConfiguration/AddBuildingModal';
import RemoteAccessAuditConfigurationContext from '@totem/components/services/remoteAccess/auditConfiguration/RemoteAccessAuditConfigurationContext';
import {
  BuildingConfiguration,
  RequestRemoteAccessBuildingConfigurationUpdate,
  RequestRemoteAccessOrganizationConfigurationUpdate,
} from '@totem/components/services/remoteAccess/auditConfiguration/types';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import { sortStringAscending } from '@totem/utilities/tableUtilities';

import './auditConfiguration.css';

type BuildingEditState = {
  nextAudit: string;
  cadenceMonths: number;
  dirty: boolean;
};

const FormItem = Form.Item;

const formatDate = (dateIn: string) => {
  if (!dateIn || dateIn === '0001-01-01T00:00:00Z') {
    return '-';
  }
  return new Date(dateIn).toLocaleDateString();
};

const toDayjs = (dateIn: string) => {
  if (!dateIn || dateIn === '0001-01-01T00:00:00Z') {
    return null;
  }
  const parsed = dayjs(dateIn);
  return parsed.isValid() ? parsed : null;
};

const RemoteAccessAuditConfigurationContent = () => {
  const {
    config,
    loading,
    canView,
    isSending,
    onUpdateOrganization,
    onUpdateBuilding,
  } = useContext(RemoteAccessAuditConfigurationContext);

  const [orgNextAudit, setOrgNextAudit] = useState<string>('');
  const [orgCadenceMonths, setOrgCadenceMonths] = useState<number>(1);
  const [isOrgSaving, setIsOrgSaving] = useState<boolean>(false);
  const [buildingEdits, setBuildingEdits] = useState<
    Record<string, BuildingEditState>
  >({});
  const [buildingSaves, setBuildingSaves] = useState<Record<string, boolean>>(
    {},
  );
  const [addModalOpen, setAddModalOpen] = useState<boolean>(false);
  const [isAdding, setIsAdding] = useState<boolean>(false);

  useEffect(() => {
    if (!config) {
      setOrgNextAudit('');

      setOrgCadenceMonths(1);

      setBuildingEdits({});
      return;
    }
    setOrgNextAudit(config.nextAudit);

    setOrgCadenceMonths(config.cadenceMonths);

    const nextEdits: Record<string, BuildingEditState> = {};
    (config.buildings || []).forEach((building) => {
      nextEdits[building.building.id] = {
        nextAudit: building.nextAudit,
        cadenceMonths: building.cadenceMonths,
        dirty: false,
      };
    });

    setBuildingEdits(nextEdits);
  }, [config]);

  const buildingRows = useMemo(() => config?.buildings || [], [config]);

  const existingBuildingIds = useMemo(
    () => buildingRows.map((building) => building.building.id),
    [buildingRows],
  );

  const orgDirty = useMemo(() => {
    if (!config) {
      return false;
    }
    return (
      config.nextAudit !== orgNextAudit ||
      config.cadenceMonths !== orgCadenceMonths
    );
  }, [config, orgNextAudit, orgCadenceMonths]);

  if (!canView) {
    return (
      <div className="audit-config-message">
        You do not have access to view remote access audit configuration.
      </div>
    );
  }

  if (loading && !config) {
    return <div className="audit-config-message">Loading configuration...</div>;
  }

  const updateBuildingEdit = (
    building: BuildingConfiguration,
    updates: Partial<BuildingEditState>,
  ) => {
    setBuildingEdits((prevState) => {
      const current = prevState[building.building.id] || {
        nextAudit: building.nextAudit,
        cadenceMonths: building.cadenceMonths,
        dirty: false,
      };
      const nextState = {
        ...current,
        ...updates,
      };
      const isDirty =
        nextState.nextAudit !== building.nextAudit ||
        nextState.cadenceMonths !== building.cadenceMonths;
      return {
        ...prevState,
        [building.building.id]: {
          ...nextState,
          dirty: isDirty,
        },
      };
    });
  };

  const handleSaveOrganization = async () => {
    if (!onUpdateOrganization || !orgNextAudit || !orgCadenceMonths) {
      return;
    }
    setIsOrgSaving(true);
    const payload: RequestRemoteAccessOrganizationConfigurationUpdate = {
      nextAudit: orgNextAudit,
      cadenceMonths: orgCadenceMonths,
    };
    await onUpdateOrganization(payload);

    setIsOrgSaving(false);
  };

  const handleSaveBuilding = async (building: BuildingConfiguration) => {
    if (!onUpdateBuilding || !config) {
      return;
    }
    const editState = buildingEdits[building.building.id];
    if (!editState || !editState.nextAudit || !editState.cadenceMonths) {
      return;
    }
    setBuildingSaves((prevState) => ({
      ...prevState,
      [building.building.id]: true,
    }));
    const payload: RequestRemoteAccessBuildingConfigurationUpdate = {
      organization: config.organization,
      building: building.building,
      nextAudit: editState.nextAudit,
      cadenceMonths: editState.cadenceMonths,
    };
    await onUpdateBuilding(payload);

    setBuildingSaves((prevState) => ({
      ...prevState,
      [building.building.id]: false,
    }));
  };

  const handleAddBuilding = async ({
    building,
    nextAudit,
    cadenceMonths,
  }: {
    building: BuildingConfiguration['building'];
    nextAudit: string;
    cadenceMonths: number;
  }) => {
    if (!onUpdateBuilding || !config) {
      return;
    }
    setIsAdding(true);
    const payload: RequestRemoteAccessBuildingConfigurationUpdate = {
      organization: config.organization,
      building,
      nextAudit,
      cadenceMonths,
    };
    const success = await onUpdateBuilding(payload);
    setIsAdding(false);
    if (success) {
      setAddModalOpen(false);
    }
  };

  const columns: ColumnProps<BuildingConfiguration>[] = [
    {
      title: 'Building',
      dataIndex: 'building',
      key: 'building',
      render: (_, record) => record.building?.name || '-',
      sorter: (compA, compB) =>
        sortStringAscending(
          compA.building?.name || '',
          compB.building?.name || '',
        ),
    },
    {
      title: 'Last Audit',
      dataIndex: 'lastAudit',
      key: 'lastAudit',
      render: (_, record) => formatDate(record.lastAudit),
    },
    {
      title: 'Next Audit',
      dataIndex: 'nextAudit',
      key: 'nextAudit',
      width: 200,
      render: (_, record) => {
        const editState = buildingEdits[record.building.id];
        return (
          <DatePicker
            value={toDayjs(editState?.nextAudit || record.nextAudit)}
            onChange={(value) => {
              if (value) {
                updateBuildingEdit(record, {
                  nextAudit: value.toISOString(),
                });
              }
            }}
            style={{ width: '100%' }}
          />
        );
      },
    },
    {
      title: 'Cadence (months)',
      dataIndex: 'cadenceMonths',
      key: 'cadenceMonths',
      width: 200,
      render: (_, record) => {
        const editState = buildingEdits[record.building.id];
        return (
          <InputNumber
            min={1}
            value={editState?.cadenceMonths ?? record.cadenceMonths}
            onChange={(value) => {
              if (typeof value === 'number') {
                updateBuildingEdit(record, {
                  cadenceMonths: value,
                });
              }
            }}
            style={{ width: '100%' }}
          />
        );
      },
    },
    {
      title: 'Actions',
      dataIndex: 'actions',
      key: 'actions',
      width: 200,
      render: (_, record) => {
        const editState = buildingEdits[record.building.id];
        const isSaving = buildingSaves[record.building.id];
        return (
          <Button
            type="primary"
            onClick={() => handleSaveBuilding(record)}
            disabled={!editState?.dirty || isSending || isSaving}
          >
            Save
          </Button>
        );
      },
    },
  ];

  return (
    <div className="audit-config-container">
      <div className="audit-config-section">
        <Divider titlePlacement={'start'}>
          Organization Configuration: ({config?.organization?.name || '-'})
        </Divider>
        <Form layout="vertical" className="audit-config-form">
          <FormItem label="Next Audit" colon={false}>
            <DatePicker
              width={200}
              value={toDayjs(orgNextAudit)}
              onChange={(value) => {
                if (value) {
                  setOrgNextAudit(value.toISOString());
                }
              }}
            />
          </FormItem>
          <FormItem label="Cadence (months)" colon={false}>
            <InputNumber
              min={1}
              value={orgCadenceMonths}
              onChange={(value) => {
                if (typeof value === 'number') {
                  setOrgCadenceMonths(value);
                }
              }}
              width={200}
            />
          </FormItem>
          <div className="audit-config-form-actions">
            <Button
              type="primary"
              onClick={handleSaveOrganization}
              disabled={!orgDirty || isSending || isOrgSaving}
            >
              Save Organization
            </Button>
          </div>
        </Form>
      </div>

      <div className="audit-config-divider" />
      <br />

      <div className="audit-config-section">
        <Divider titlePlacement={'start'}>Building Configuration</Divider>
        <Flex justify={'flex-end'} align={'center'}>
          <Button type="primary" onClick={() => setAddModalOpen(true)}>
            Add Building
          </Button>
        </Flex>
        <br />
        <IBTable
          className="audit-config-table"
          showSorterTooltip
          columns={columns}
          dataSource={buildingRows}
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
          }}
          rowKey={(record) => record.building.id}
        />
      </div>
      {config && (
        <AddBuildingModal
          open={addModalOpen}
          organizationConfig={config}
          existingBuildingIds={existingBuildingIds}
          defaultNextAudit={orgNextAudit || config.nextAudit}
          defaultCadenceMonths={orgCadenceMonths || config.cadenceMonths}
          isSaving={isAdding || isSending}
          onCancel={() => setAddModalOpen(false)}
          onSubmit={handleAddBuilding}
        />
      )}
    </div>
  );
};

export default RemoteAccessAuditConfigurationContent;
